import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Terminal, MagnifyingGlass, Sparkle, CircleNotch } from "@phosphor-icons/react";

export const BaseCommands = ({ onInsert }) => {
  const [open, setOpen] = useState(false);
  const [cmds, setCmds] = useState([]);
  const [files, setFiles] = useState("");
  const [q, setQ] = useState("");
  const [missing, setMissing] = useState(0);
  const [enriching, setEnriching] = useState(false);
  const [progress, setProgress] = useState("");

  const load = () =>
    api.baseCommands()
      .then((d) => { setCmds(d.commands || []); setFiles(d.files || ""); setMissing(d.missing || 0); })
      .catch(() => toast.error("Could not load base commands."));

  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line

  const enrich = async () => {
    setEnriching(true);
    setProgress("Starting…");
    try {
      const { job_id } = await api.enrichBaseCommands();
      const started = Date.now();
      while (Date.now() - started < 600000) {
        await new Promise((r) => setTimeout(r, 2500));
        const s = await api.generationStatus(job_id);
        if (s?.progress?.total) setProgress(`Describing ${s.progress.done}/${s.progress.total}…`);
        if (s.status === "done") { await load(); toast.success("Descriptions enriched with AI"); break; }
        if (s.status === "error") { toast.error(s.error || "Enrichment failed"); break; }
      }
    } catch { toast.error("Enrichment failed"); }
    finally { setEnriching(false); setProgress(""); }
  };

  const ql = q.trim().toLowerCase();
  const filtered = ql
    ? cmds.filter((c) => c.name.toLowerCase().includes(ql) || (c.desc || "").toLowerCase().includes(ql))
    : cmds;
  const described = cmds.filter((c) => c.desc).length;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="base-commands-btn"
          className="h-8 rounded-[3px] gap-1.5 text-xs font-mono text-[var(--text-dim)] hover:text-[var(--text)] hover:bg-[var(--surface-2)]">
          <Terminal size={14} weight="bold" /> Base Cmds
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)] max-w-2xl rounded-[4px]" data-testid="base-commands-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono text-base flex items-center gap-2"><Terminal size={18} weight="bold" className="text-[var(--primary)]" />Base Program Commands</DialogTitle>
          <DialogDescription className="text-[var(--text-dim)] text-xs">
            The {cmds.length} existing <code className="text-[var(--primary)]">c:</code> commands in {files || "the base program"} ({described} described). Every generation reuses these EXACT names instead of inventing new ones. Click a command to reference it in your prompt.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-dim)]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by command or description…" data-testid="base-commands-search"
              className="h-8 pl-8 rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-xs" />
          </div>
          {missing > 0 && (
            <Button size="sm" onClick={enrich} disabled={enriching} data-testid="enrich-btn"
              className="h-8 rounded-[3px] text-xs gap-1.5 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white shrink-0">
              {enriching ? <CircleNotch size={13} className="animate-spin" /> : <Sparkle size={13} weight="fill" />}
              {enriching ? progress : `Describe ${missing} with AI`}
            </Button>
          )}
        </div>
        <div className="text-[10px] font-mono text-[var(--text-dim)]">{filtered.length} shown</div>
        <ScrollArea className="h-[320px] border border-[var(--border)] rounded-[3px]" data-testid="base-commands-list">
          {filtered.length === 0 && <div className="p-3 text-[11px] text-[var(--text-dim)]">No matching commands.</div>}
          {filtered.map((c) => (
            <div key={c.name} className="flex items-center gap-3 px-2.5 py-1.5 border-b border-[var(--border)]/40 hover:bg-[var(--surface-2)]" data-testid={`base-cmd-row-${c.name}`}>
              <button data-testid={`base-cmd-${c.name}`}
                onClick={() => { onInsert?.(c.name); toast.success(`Referenced c:${c.name} in prompt`); }}
                className="shrink-0 h-6 min-w-[70px] px-2 rounded-[3px] border border-[var(--border)] bg-[var(--bg)] text-[11px] font-mono text-[var(--primary)] hover:border-[var(--primary)] transition-colors text-left">
                c:{c.name}
              </button>
              <span className="text-[11px] text-[var(--text-dim)] truncate flex items-center gap-1.5">
                {c.ai && <Sparkle size={10} weight="fill" className="text-[var(--primary)] shrink-0" title="AI-generated description" />}
                {c.desc || <span className="opacity-40">— no description in source</span>}
              </span>
            </div>
          ))}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
